import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Lock, User, Cpu } from 'lucide-react';

export default function Login() {
  // Estados para controlar os inputs
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // 1. Validação usando as variáveis de estado
    if (!username || !password) {
        toast.error("Preencha e-mail e senha.");
        setLoading(false);
        return;
    }

    try {
      // 2. Monta os dados para o FastAPI (x-www-form-urlencoded)
      const params = new URLSearchParams();
      // O FastAPI espera o campo 'username', então passamos o email (username state) para ele
      params.append('username', username); 
      params.append('password', password);

      // 3. Envia a requisição
      const res = await axiosInstance.post('/auth/login', params, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      // 4. Sucesso: Salva o usuário
      if (res.data.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
      }

      toast.success('Login realizado com sucesso!');
      
      // Pequeno delay para garantir que o cookie foi gravado antes de redirecionar
      setTimeout(() => {
          window.location.href = '/'; 
      }, 500);

    } catch (error) {
      console.error(error);
      setLoading(false); // Importante: Desliga o loading se der erro
      
      if (error.response?.status === 401) {
          toast.error('E-mail ou senha incorretos.');
      } else if (error.response?.status === 422) {
          console.log("Erro de validação (422):", error.response.data);
          toast.error('Erro de formato (422). Verifique o console.');
      } else {
          toast.error('Erro ao conectar com o servidor.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-4">
      
      {/* Background Glows */}
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-600/20 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-cyan-500/10 blur-[100px] rounded-full pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        
        <div className="bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-[2rem] shadow-2xl shadow-black/50 p-8 md:p-10">
          
          <div className="text-center mb-12">
            <h1 className="text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-indigo-300 to-cyan-300">
                OPERYA
              </span>
            </h1>
            <div className="flex items-center justify-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-[0.2em]">
               <Cpu size={14} className="text-cyan-400" />
               <span>A plataforma para agências que buscam eficiência absoluta</span>
            </div>
          </div>

          <form data-testid="login-form" onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300 font-medium ml-1">Usuário</Label>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                  <User size={18} />
                </div>
                <Input
                  data-testid="username-input"
                  id="username"
                  type="email"
                  name="email" // Importante para autocomplete
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-10 h-12 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-indigo-500 focus:ring-indigo-500/20 rounded-xl transition-all"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                <Label htmlFor="password" className="text-slate-300 font-medium">Senha</Label>
                <a href="#" className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">Esqueceu?</a>
              </div>
              <div className="relative group">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                  <Lock size={18} />
                </div>
                <Input
                  data-testid="password-input"
                  id="password"
                  type="password"
                  name="password" // ADICIONADO: Importante para navegadores
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 h-12 bg-slate-950/50 border-slate-700 text-white placeholder:text-slate-600 focus:border-indigo-500 focus:ring-indigo-500/20 rounded-xl transition-all"
                  placeholder="Sua senha"
                />
              </div>
            </div>

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl shadow-lg shadow-indigo-600/25 transition-all active:scale-[0.98] mt-4 text-base"
            >
              {loading ? 'Conectando...' : (
                <span className="flex items-center justify-center gap-2">
                  Entrar no Sistema <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 text-center border-t border-white/5 pt-6">
            <p className="text-sm text-slate-500">
              Ainda não é cliente?{' '}
              <button
                onClick={() => navigate('/register')}
                className="font-bold text-indigo-400 hover:text-indigo-300 hover:underline transition-colors"
              >
                Solicitar Demo
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}